import React, { useCallback, useEffect, useMemo, useState } from 'react'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { Link, useNavigate } from 'react-router-dom'
import { activateRbacAccount, validateActivationToken } from '../rbac/services/rbacApi'
import { UI_COLOR_SUCCESS_TEXT } from '../constants/uiColors'
import TimedToastAlert from '../components/alerts/TimedToastAlert'
import {
  ACTIVATE_ACCOUNT_DEFAULT_SUBTITLE,
  ACTIVATE_ACCOUNT_PASSWORD_ERROR_MESSAGE,
  ACTIVATE_ACCOUNT_PERSONALIZED_SUBTITLE,
  ACTIVATE_ACCOUNT_SUBTITLE_BY_STATUS,
  ACTIVATE_ACCOUNT_SUCCESS_ALERT_MESSAGE
} from './constants/activateAccountPageConstants'
import LoginPasswordField from './shared/LoginPasswordField'
import LoginShell from './shared/LoginShell'
import PasswordRulesPanel from './shared/PasswordRulesPanel'
import { isPasswordValid } from './shared/passwordValidation'

function getActivationValidationStatus(err) {
  const serviceStatus = err.payload?.status
  if (serviceStatus === 'invalid' || serviceStatus === 'expired') {
    return { status: serviceStatus, user: err.payload?.user || null }
  }

  return { status: 'validation_error', user: null }
}

function getActivateAccountSubtitle(status, user) {
  switch (status) {
    case 'validating':
    case 'validation_error':
    case 'invalid':
    case 'expired':
    case 'success':
      return ACTIVATE_ACCOUNT_SUBTITLE_BY_STATUS[status]
    default:
      return user?.name
        ? ACTIVATE_ACCOUNT_PERSONALIZED_SUBTITLE(user.name)
        : ACTIVATE_ACCOUNT_DEFAULT_SUBTITLE
  }
}

export default function ActivateAccountPage() {
  const navigate = useNavigate()
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') || '', [])
  const [status, setStatus] = useState(token ? 'validating' : 'invalid')
  const [user, setUser] = useState(null)
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false)
  const [error, setError] = useState('')
  const [alertId, setAlertId] = useState(0)
  const canSubmitPassword = isPasswordValid(password, passwordConfirmation)

  const validateToken = useCallback(async () => {
    if (!token) return

    setStatus('validating')
    setUser(null)

    try {
      const response = await validateActivationToken(token)
      setStatus(response.status || 'valid')
      setUser(response.user)
    } catch (err) {
      const { status: nextStatus, user: nextUser } = getActivationValidationStatus(err)
      setStatus(nextStatus)
      setUser(nextUser)
    }
  }, [token])

  useEffect(() => {
    if (!token) return undefined
    validateToken()
  }, [token, validateToken])

  const submit = async (event) => {
    event.preventDefault()
    setError('')

    if (!isPasswordValid(password, passwordConfirmation)) {
      setError(ACTIVATE_ACCOUNT_PASSWORD_ERROR_MESSAGE)
      setAlertId(Date.now())
      return
    }

    try {
      setStatus('submitting')
      const response = await activateRbacAccount({
        token,
        password,
        password_confirmation: passwordConfirmation
      })
      setUser(response.user)
      setStatus('success')
    } catch (err) {
      const nextStatus = err.payload?.status || 'error'
      setStatus(['expired', 'invalid'].includes(nextStatus) ? nextStatus : 'error')
      setError(err.payload?.errors?.join(', ') || err.message)
      setAlertId(Date.now())
    }
  }

  const subtitle = getActivateAccountSubtitle(status, user)

  return (
    <LoginShell eyebrow="Invitación" title="Activa tu cuenta" subtitle={subtitle}>
      {status === 'validating' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>Validando invitación...</Typography>
        </Box>
      ) : null}

      {status === 'validation_error' ? (
        <button className="login-submit-btn" type="button" onClick={validateToken}>
          <span className="login-submit-label">Reintentar</span>
        </button>
      ) : null}

      {status === 'invalid' || status === 'expired' ? (
        <p className="login-form-footer">
          <Link className="login-forgot-link" to="/">
            Iniciar sesión
          </Link>
        </p>
      ) : null}

      {status === 'success' ? (
        <>
          <div className="login-form-alert" aria-live="polite">
            <TimedToastAlert
              id="activate-success"
              variant="inline"
              severity="success"
              message={ACTIVATE_ACCOUNT_SUCCESS_ALERT_MESSAGE}
              onClose={() => navigate('/', { replace: true })}
            />
          </div>
          <p className="login-form-footer">
            <Link className="login-forgot-link" to="/">
              Iniciar sesión
            </Link>
          </p>
        </>
      ) : null}

      {['valid', 'submitting', 'error'].includes(status) ? (
        <>
          {user?.email ? (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ color: '#5B6472', fontSize: 13, fontWeight: 600, mb: 0.75 }}>
                Correo
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  px: 1.75,
                  py: 1.5,
                  borderRadius: '14px',
                  border: '1.5px solid #D5DEE7',
                  backgroundColor: '#F8FAFC'
                }}
              >
                <Typography sx={{ color: '#1A2733', fontSize: 15, wordBreak: 'break-all' }}>
                  {user.email}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: UI_COLOR_SUCCESS_TEXT,
                    flexShrink: 0
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Verificado</Typography>
                </Box>
              </Box>
            </Box>
          ) : null}

          <form className="login-main-form" onSubmit={submit} noValidate>
            {error ? (
              <div className="login-form-alert" aria-live="polite">
                <TimedToastAlert
                  id={alertId}
                  variant="inline"
                  severity="error"
                  message={error}
                  onClose={() => setError('')}
                />
              </div>
            ) : null}

            <LoginPasswordField
              id="activate-password"
              label="Nueva contraseña"
              name="password"
              value={password}
              onChange={(event) => setPassword(event.target.value.slice(0, 15))}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword((current) => !current)}
            />
            <LoginPasswordField
              id="activate-password-confirmation"
              label="Confirmar contraseña"
              name="passwordConfirmation"
              value={passwordConfirmation}
              onChange={(event) => setPasswordConfirmation(event.target.value.slice(0, 15))}
              showPassword={showPasswordConfirmation}
              onTogglePassword={() => setShowPasswordConfirmation((current) => !current)}
            />
            <PasswordRulesPanel password={password} passwordConfirmation={passwordConfirmation} />
            <button
              className={`login-submit-btn${status === 'submitting' ? ' is-loading' : ''}`}
              type="submit"
              disabled={status === 'submitting' || !canSubmitPassword}
            >
              <span className="login-submit-label">
                {status === 'submitting' ? 'Activando...' : 'Activar cuenta'}
              </span>
              <span className="login-submit-spinner" aria-hidden="true" />
            </button>
          </form>
        </>
      ) : null}
    </LoginShell>
  )
}
